import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Megaphone, UsersThree, MapPin, PaperPlaneRight, Check } from "@phosphor-icons/react";

export default function MassSms() {
  const [target, setTarget] = useState("all");
  const [selectedSites, setSelectedSites] = useState([]);
  const [sites, setSites] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/sites").then((r) => setSites(r.data)).catch(() => {});
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  function toggleSite(name) {
    setSelectedSites((prev) =>
      prev.includes(name) ? prev.filter((s) => s !== name) : [...prev, name]
    );
  }

  const recipientCount =
    target === "all"
      ? customers.length
      : customers.filter((c) => (c.tags || []).some((t) => selectedSites.includes(t))).length;

  async function send() {
    if (!body.trim()) return toast.error("Message body is empty");
    if (target === "site" && selectedSites.length === 0)
      return toast.error("Select at least one list / tag");
    setSending(true);
    try {
      const { data } = await api.post("/messages/mass", {
        body: body.trim(),
        target,
        sites: target === "site" ? selectedSites : undefined,
      });
      toast.success(`Sent to ${data.sent} of ${data.total} customers${data.failed ? `, ${data.failed} failed` : ""}`);
      if (data.errors?.length) {
        data.errors.forEach((e) => toast.error(e));
      }
      setBody("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="h-16 flex items-center px-6 border-b border-border gap-3">
        <Megaphone size={22} weight="fill" className="text-primary" />
        <h1 className="text-xl font-semibold tracking-tight">Mass SMS</h1>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-3xl space-y-6">
          {/* Target selection */}
          <div className="space-y-3">
            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
              Recipients
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                data-testid="target-all"
                onClick={() => setTarget("all")}
                className={`p-4 rounded-sm border text-left transition-colors duration-100 ${
                  target === "all"
                    ? "border-primary bg-primary/10"
                    : "border-border hover:bg-accent"
                }`}
              >
                <UsersThree size={22} className={target === "all" ? "text-primary" : "text-muted-foreground"} />
                <div className="mt-2 font-medium text-sm">All customers</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Blast every synced customer
                </div>
              </button>
              <button
                data-testid="target-site"
                onClick={() => setTarget("site")}
                className={`p-4 rounded-sm border text-left transition-colors duration-100 ${
                  target === "site"
                    ? "border-primary bg-primary/10"
                    : "border-border hover:bg-accent"
                }`}
              >
                <MapPin size={22} className={target === "site" ? "text-primary" : "text-muted-foreground"} />
                <div className="mt-2 font-medium text-sm">Grouped by list</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Target one or more UISP tags / lists
                </div>
              </button>
            </div>
          </div>

          {target === "site" && (
            <div className="space-y-2 animate-fade-up">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                  Lists / Tags
                </Label>
                {selectedSites.length > 0 && (
                  <button
                    data-testid="clear-sites"
                    onClick={() => setSelectedSites([])}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Clear ({selectedSites.length})
                  </button>
                )}
              </div>
              {sites.length === 0 ? (
                <div className="text-sm text-muted-foreground border border-border rounded-sm p-3">
                  No lists found. Sync customers from UISP to pull in their tags.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {sites.map((s) => {
                    const active = selectedSites.includes(s.name);
                    return (
                      <button
                        key={s.name}
                        data-testid={`site-chip-${s.name}`}
                        onClick={() => toggleSite(s.name)}
                        className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-sm border transition-colors duration-100 ${
                          active
                            ? "border-primary bg-primary/15 text-primary"
                            : "border-border hover:bg-accent text-foreground"
                        }`}
                      >
                        {active ? <Check size={14} weight="bold" /> : <MapPin size={14} />}
                        {s.name}
                        <span className="text-xs opacity-60 font-mono">{s.count}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Message body */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
              Message
            </Label>
            <Textarea
              data-testid="mass-message-input"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your broadcast message…"
              rows={6}
              className="rounded-sm resize-none"
            />
            <div className="text-xs text-muted-foreground font-mono">
              {body.length} chars · ~{Math.max(1, Math.ceil(body.length / 160))} segment(s)
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-border pt-5">
            <div className="text-sm text-muted-foreground">
              Sending to{" "}
              <span className="text-primary font-semibold font-mono" data-testid="recipient-count">
                {recipientCount}
              </span>{" "}
              customer{recipientCount === 1 ? "" : "s"}
              {target === "site" && selectedSites.length > 1 && (
                <span className="text-xs"> (deduplicated across lists)</span>
              )}
            </div>
            <Button
              data-testid="mass-send-button"
              onClick={send}
              disabled={sending || !body.trim() || recipientCount === 0}
              className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground gap-2"
            >
              {sending ? "Sending…" : "Send broadcast"}
              {!sending && <PaperPlaneRight size={16} weight="fill" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

